/**
 * Data blocks of pages: record context, the live query of a data view and its materialised result.
 * Owner: doc-engine (wave 1), page-blocks-ai (wave 2 consumer).
 */
import { z } from 'zod';
import { QueryRequest } from '../query';
import { FieldPath, andFilters } from '../filter';
import { AppError } from '../errors';

export const DocumentContext = z
  .object({ objectKey: z.string().regex(/^[a-z][a-z0-9_]{0,62}$/), recordId: z.uuid() })
  .strict();
export type DocumentContext = z.infer<typeof DocumentContext>;
export const DataBlockQuery = QueryRequest.omit({ cursor: true, includeSources: true })
  .partial()
  .extend({
    limit: z.number().int().min(1).max(100).optional(),
    contextField: FieldPath.optional(),
  })
  .strict();
export type DataBlockQuery = z.infer<typeof DataBlockQuery>;
export function resolveDataQuery(query: DataBlockQuery = {}, context?: DocumentContext | null) {
  const { contextField, ...request } = query;
  if (contextField && !context)
    throw new AppError('validation_failed', 'This data block needs a record context');
  return QueryRequest.parse({
    ...request,
    count: 'exact',
    filter: andFilters(
      request.filter,
      contextField ? { field: contextField, op: 'eq', value: context!.recordId } : null,
    ),
  });
}
/** Plain display text shared by authenticated and public data blocks; never exposes raw metadata. */
export function documentValueText(value: unknown, config?: Record<string, unknown>): string {
  if (value == null) return '';
  const options = config?.options as Array<{ key: string; label: string }> | undefined;
  if (Array.isArray(value)) return value.map((v) => documentValueText(v, config)).join(', ');
  if (typeof value !== 'object') return options?.find((o) => o.key === value)?.label ?? String(value);
  const v = value as Record<string, unknown>;
  if ('title' in v) return String(v.title ?? '');
  // An entry of a multi-value field (`{ value, label?, primary }`) shows its value, not its label ("Work").
  if ('value' in v && typeof v.primary === 'boolean') return String(v.value ?? '');
  if ('label' in v) return String(v.label ?? '');
  if ('value' in v) return `${v.value ?? ''}${v.currency ? ' ' + v.currency : ''}`;
  return '';
}
export const DataResult = z.object({
  title: z.string().max(160),
  mode: z.enum(['table', 'count', 'cards', 'chart', 'metric']),
  columns: z.array(z.object({ key: z.string(), label: z.string() })).max(200),
  rows: z.array(z.record(z.string(), z.string())).max(100),
  count: z.number().nullable(),
  totals: z.record(z.string(), z.union([z.number(), z.string(), z.null()])),
  groups: z.array(z.object({ label: z.string(), value: z.number() })).max(500),
});
export type DataResult = z.infer<typeof DataResult>;
